import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import AtlasIcon from './AtlasIcon';
import ChangePasswordModal from '../../ChangePasswordModal';
import UsersAdminModal from '../../UsersAdminModal';
import OfflineBadge from './OfflineBadge';
import AtlasEmptyState from './AtlasEmptyState';
import { t } from '../../i18n';
import { findLastPeriodWithData } from '../../findLastPeriodWithData';
import { getPresetDates, detectActivePreset, PERIOD_PRESETS } from '../../TopBar';
import './Buttons.css';
import './AtlasScopeBar.css';

const THEME_KEY = 'percurso:theme';
const THEME_INK = 'suite-glass';

// Barra de escopo nativa do Atlas Vivo (fase 8 — docs/plans/overhaul-ux-atlas-vivo.md).
// Substitui o slot que antes renderizava o <TopBar> antigo inteiro (chips de
// tipo, presets de navegação, menu "Mais"...) por um cabeçalho mínimo igual
// ao render de referência: marca, busca, período e usuário. As ações que o
// TopBar escondia no menu "Mais" (Insights, Memory Book, GitHub, Importar)
// já viraram cards nos workspaces Histórias/Dados — não duplicamos aqui.
function AtlasScopeBar({
  currentFilters,
  onFilterChange,
  onOpenSearch,
  user,
  onLogout,
  offline,
  offlineCachedAt,
  canInstall,
  onInstallApp,
  periodEmpty = false,
  loadedData,
}) {
  const [periodOpen, setPeriodOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [inkTheme, setInkTheme] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem(THEME_KEY) === THEME_INK,
  );
  const { startDate, endDate } = currentFilters || {};
  const activePreset = detectActivePreset(startDate, endDate);
  const isAdmin = user && user.role === 'admin';
  const showOfflineBadge = Boolean(offline || offlineCachedAt);

  useEffect(() => {
    const root = document.documentElement;
    if (inkTheme) {
      root.setAttribute('data-theme', THEME_INK);
      try { localStorage.setItem(THEME_KEY, THEME_INK); } catch { /* noop */ }
    } else {
      root.removeAttribute('data-theme');
      try { localStorage.removeItem(THEME_KEY); } catch { /* noop */ }
    }
  }, [inkTheme]);

  const lastPeriodWithData = useMemo(
    () => findLastPeriodWithData(loadedData || {}, { startDate, endDate }),
    [loadedData, startDate, endDate]
  );

  const shiftPeriod = (direction) => {
    if (!startDate || !endDate) return;
    const start = moment(startDate);
    const end = moment(endDate);
    const durationDays = Math.max(1, end.diff(start, 'days') + 1);
    const delta = direction === 'previous' ? -durationDays : durationDays;
    onFilterChange({
      ...currentFilters,
      startDate: start.clone().add(delta, 'days').format('YYYY-MM-DD'),
      endDate: end.clone().add(delta, 'days').format('YYYY-MM-DD'),
    });
  };

  const applyPreset = (key) => {
    onFilterChange({ ...currentFilters, ...getPresetDates(key) });
    setPeriodOpen(false);
  };

  const goToLastPeriodWithData = () => {
    if (!lastPeriodWithData || !onFilterChange) return;
    onFilterChange({
      ...currentFilters,
      startDate: lastPeriodWithData.startDate,
      endDate: lastPeriodWithData.endDate,
    });
  };

  const initials = user && user.username
    ? user.username.slice(0, 2).toUpperCase()
    : '··';

  const openAccountMenu = () => {
    setPeriodOpen(false);
    setAccountMenuOpen((open) => !open);
  };

  const openPasswordModal = () => {
    setAccountMenuOpen(false);
    setPasswordModalOpen(true);
  };

  const openUsersModal = () => {
    setAccountMenuOpen(false);
    setUsersModalOpen(true);
  };

  const handleLogout = () => {
    setAccountMenuOpen(false);
    if (onLogout) onLogout();
  };

  const handleInstall = () => {
    setAccountMenuOpen(false);
    if (onInstallApp) onInstallApp();
  };

  return (
    <div className="av-scopebar-stack">
      <div className="av-scopebar-inner">
        <div className="av-scopebar-wordmark">
          <strong>Timeline</strong>
          <span>{t('web.scope.tagline')}</span>
        </div>

        {showOfflineBadge ? (
          <OfflineBadge cachedAt={offlineCachedAt} compact />
        ) : null}

        {onOpenSearch && (
          <button type="button" className="av-btn av-btn--ghost av-scopebar-search" onClick={onOpenSearch} aria-label={t('web.scope.search.a11y')}>
            <AtlasIcon name="search" size={17} />
            <span>{t('web.scope.search.placeholder')}</span>
            <kbd>⌘K</kbd>
          </button>
        )}

        <div className="av-scopebar-date">
          <button type="button" className="av-btn av-btn--icon av-icon-btn" title={t('web.scope.prevPeriod')} onClick={() => shiftPeriod('previous')}>
            <AtlasIcon name="chevron-left" size={16} />
          </button>
          <button
            type="button"
            className="av-btn av-btn--ghost av-scopebar-date-btn"
            aria-expanded={periodOpen}
            onClick={() => setPeriodOpen((open) => !open)}
          >
            <span>{startDate ? moment(startDate).format('DD MMM YYYY') : '—'}</span>
            <strong>— {endDate ? moment(endDate).format('DD MMM YYYY') : '—'}</strong>
          </button>
          <button type="button" className="av-btn av-btn--icon av-icon-btn" title={t('web.scope.nextPeriod')} onClick={() => shiftPeriod('next')}>
            <AtlasIcon name="chevron-right" size={16} />
          </button>

          {periodOpen && (
            <div className="av-scopebar-period-menu" role="menu" aria-label={t('web.scope.quickPeriods')}>
              {PERIOD_PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  role="menuitem"
                  className={`av-btn av-btn--ghost av-scopebar-period-item${activePreset === preset.key ? ' is-active' : ''}`}
                  onClick={() => applyPreset(preset.key)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {user && (
          <div className="av-scopebar-account">
            <button
              type="button"
              className="av-btn av-btn--icon av-scopebar-avatar"
              title={t('web.scope.account', { user: user.username })}
              aria-expanded={accountMenuOpen}
              aria-haspopup="menu"
              onClick={openAccountMenu}
            >
              {initials}
            </button>

            {accountMenuOpen && (
              <div className="av-scopebar-account-menu" role="menu" aria-label={t('web.scope.accountMenu')}>
                <button
                  type="button"
                  role="menuitem"
                  className="av-btn av-btn--ghost av-scopebar-account-item"
                  onClick={openPasswordModal}
                >
                  {t('web.scope.accountLabel')}
                </button>
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={inkTheme}
                  className="av-btn av-btn--ghost av-scopebar-account-item"
                  onClick={() => setInkTheme((enabled) => !enabled)}
                >
                  Tema ink
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    role="menuitem"
                    className="av-btn av-btn--ghost av-scopebar-account-item"
                    onClick={openUsersModal}
                  >
                    {t('web.scope.users')}
                  </button>
                )}
                {canInstall && (
                  <button
                    type="button"
                    role="menuitem"
                    className="av-btn av-btn--ghost av-scopebar-account-item"
                    onClick={handleInstall}
                  >
                    {t('web.scope.install')}
                  </button>
                )}
                <button
                  type="button"
                  role="menuitem"
                  className="av-btn av-btn--ghost av-btn--destructive av-scopebar-account-item av-scopebar-logout"
                  onClick={handleLogout}
                >
                  {t('web.scope.logout')}
                </button>
              </div>
            )}
          </div>
        )}

        {passwordModalOpen && (
          <ChangePasswordModal
            onClose={() => setPasswordModalOpen(false)}
            onSuccess={() => setPasswordModalOpen(false)}
          />
        )}

        {usersModalOpen && (
          <UsersAdminModal onClose={() => setUsersModalOpen(false)} />
        )}
      </div>

      {periodEmpty ? (
        <div className="av-scopebar-empty">
          <AtlasEmptyState
            compact
            iconName="map"
            title={t('atlas.emptyPeriod.title')}
            message={t('atlas.emptyPeriod.message', { start: startDate || '—', end: endDate || '—' })}
            actionLabel={lastPeriodWithData ? t('action.goToLastPeriodWithData') : undefined}
            onAction={lastPeriodWithData ? goToLastPeriodWithData : undefined}
            actionVariant="secondary"
          />
        </div>
      ) : null}
    </div>
  );
}

export default AtlasScopeBar;
